import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import { refreshSession } from '../store/slices/auth/authThunks';
import { globals } from '../common/globals';
import { jwtDecode } from 'jwt-decode';
import { clearPersistedState } from '../common/tokenPersistence';

export const DECKARD_API_URL = process.env.REACT_APP_DECKARD_API_URL;
export const DIGIFIER_API_URL = process.env.REACT_APP_DIGIFIER_API_URL;

const axiosInstance = axios.create();

// If a token is expired, we try and refresh it prior to making api calls
// If the token is already in the process of being refreshed
// add the request to the queue to be run once the token has been updated
let isRefreshing = false;
let requestQueue = [];

const addRequestToQueue = (resolve, reject) => {
  requestQueue.push({ resolve, reject });
};

const onRefreshCompleted = (token) => {
  requestQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(new Error('Session expired'));
    }
  });
  requestQueue = [];
};

axiosInstance.interceptors.request.use(
  async (request) => {
    try {
      const requestUrl = request.url;
      if (requestUrl.includes('/rentalscape/getUploadToken')) {
        return request;
      }
      const existingAuthHeader = request.headers.Authorization;

      // update expired token if required
      if (existingAuthHeader) {
        if (!request?.skipTokenRefresh) {
          const expiryBuffer = 60; // in seconds

          // remove "Bearer " prefix
          const existingToken = existingAuthHeader.split(' ')[1];
          const decodedToken = jwtDecode(existingToken);

          // expiration time in seconds
          const expirationTime = decodedToken.exp;
          // current time in seconds
          const currentTime = Math.floor(Date.now() / 1000); // divide by 1000 to convert to seconds

          // check if token is close to or has expired
          if (expirationTime <= currentTime + expiryBuffer) {
            if (!isRefreshing) {
              isRefreshing = true;

              // refresh tokens
              const updatedTokens = await globals.dispatch(
                refreshSession(true)
              );
              const updatedIDToken = updatedTokens?.idToken
                ? updatedTokens.idToken.toString()
                : existingToken;

              // update current request auth header with new token
              request.headers.Authorization = `Bearer ${updatedIDToken}`;

              // run queued requests with the updated token
              onRefreshCompleted(updatedIDToken);

              isRefreshing = false;
            } else {
              // If the token is already in the process of being refreshed
              // add the request to the queue to be run once the token has been updated
              return new Promise((resolve, reject) => {
                addRequestToQueue(
                  (updatedToken) => {
                    request.headers.Authorization = `Bearer ${updatedToken}`;
                    resolve(request);
                  },
                  (error) => {
                    reject(error);
                  }
                );
              });
            }
          }
        } else {
          delete request.skipTokenRefresh;
        }
      }
      return request;
    } catch (error) {
      isRefreshing = false;
      datadogRum.addError(error);
      return Promise.reject(error);
    }
  },
  (error) => {
    isRefreshing = false;
    datadogRum.addError(error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      const hasAuthHeader = error.config?.headers?.Authorization;

      if (hasAuthHeader) {
        isRefreshing = false;
        requestQueue.forEach(({ reject }) =>
          reject(new Error('Session expired'))
        );
        requestQueue = [];

        globals.dispatch({ type: 'resetRegSystem' });

        await clearPersistedState();

        const pathParts = window.location.pathname.split('/');
        const place = pathParts[1] || '';
        window.location.href = `/${place}`;

        return Promise.resolve({ data: null });
      }
    }
    datadogRum.addError(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;
